<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { useRenderLoop } from '@tresjs/core'
import type { BufferAttribute, PerspectiveCamera, Scene } from 'three'
import {
  AdditiveBlending,
  BoxGeometry,
  BufferGeometry,
  CanvasTexture,
  CatmullRomCurve3,
  CircleGeometry,
  Color,
  CylinderGeometry,
  ExtrudeGeometry,
  Float32BufferAttribute,
  FogExp2,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  Points,
  PointsMaterial,
  RepeatWrapping,
  RingGeometry,
  Shape,
  ShaderMaterial,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Vector3,
} from 'three'

// progress (0→1) from the pinned hero ScrollTrigger drives the camera
// flythrough: low skim across the board → climb → dive onto the CPU die.
const props = withDefaults(defineProps<{ progress?: number }>(), { progress: 0 })

// ?pcb2d — layout-editor debug view: top-down camera, no fog/atmosphere,
// for inspecting routing and component placement like a real PCB tool
const DEBUG_2D = new URLSearchParams(window.location.search).has('pcb2d')

const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
const isMobile = useMediaQuery('(max-width: 768px)')

const BOARD_W = 26
const BOARD_D = 18
const CHIP_SIZE = 3
const TRACE_Y = 0.115
const TRACE_WIDTH = 0.1
const PULSE_SPEED = 2.6 // world units / second

// ---------------------------------------------------------------------------
// Trace routing — buses fan out from the CPU's pins to the board edges with
// PCB-style 45° bends, Chaikin-smoothed into the rounded corners real
// copper is etched with.
// ---------------------------------------------------------------------------
interface Trace {
  pts: Vector3[] // path points: long straight runs, dense only in fillets
  arc: number[] // cumulative arc length per point
  length: number
  bend: Vector3 | null // where the diagonal meets its lane (via site)
  endCap: 'pad' | 'none' // gold dot at the far end?
  phase: number
  active: boolean
}

// Real copper is etched as straight segments with small corner fillets —
// keep the runs perfectly straight and round ONLY a ~0.2u arc at each bend.
function filletPath(raw: Vector3[], radius: number): Vector3[] {
  const out: Vector3[] = [raw[0]!]
  for (let i = 1; i < raw.length - 1; i++) {
    const prv = raw[i - 1]!
    const cur = raw[i]!
    const nxt = raw[i + 1]!
    const r = Math.min(radius, cur.distanceTo(prv) * 0.45, cur.distanceTo(nxt) * 0.45)
    const dIn = new Vector3().subVectors(cur, prv).normalize()
    const dOut = new Vector3().subVectors(nxt, cur).normalize()
    const a = new Vector3().copy(cur).addScaledVector(dIn, -r)
    const b = new Vector3().copy(cur).addScaledVector(dOut, r)
    const SEGS = 6
    for (let s = 0; s <= SEGS; s++) {
      const t = s / SEGS
      out.push(new Vector3()
        .addScaledVector(a, (1 - t) * (1 - t))
        .addScaledVector(cur, 2 * (1 - t) * t)
        .addScaledVector(b, t * t))
    }
  }
  out.push(raw[raw.length - 1]!)
  return out
}

function measure(pts: Vector3[]): { arc: number[], length: number } {
  const arc: number[] = [0]
  let total = 0
  for (let i = 1; i < pts.length; i++) {
    total += pts[i]!.distanceTo(pts[i - 1]!)
    arc.push(total)
  }
  return { arc, length: total }
}

// companion memory chip + edge-connector geometry, shared with buildScene
const DEVICE_U = 5.7
const DEVICE_HALF = 0.75
const DEVICE_PIN_SPREAD = 2.7
const FINGER_U = BOARD_D / 2 - 0.6

function toWorld(horizontal: boolean, sign: number, u: number, v: number): Vector3 {
  return horizontal ? new Vector3(sign * u, TRACE_Y, v) : new Vector3(v, TRACE_Y, sign * u)
}

function makeTrace(raw: Vector3[], bendIdx: number | null, endCap: Trace['endCap']): Trace {
  const pts = filletPath(raw, 0.2)
  const { arc, length } = measure(pts)
  return {
    pts,
    arc,
    length,
    bend: bendIdx !== null ? raw[bendIdx]!.clone() : null,
    endCap,
    phase: Math.random(),
    active: Math.random() < 0.45,
  }
}

// Each side of the CPU routes somewhere different — like a real layout:
// +x full data bus, -x grouped banks, +z companion memory chip, -z edge
// connector with gold fingers. Identical quadrants read as wallpaper.
function buildTraces(mobile: boolean): Trace[] {
  const traces: Trace[] = []
  const u0 = CHIP_SIZE / 2 + 0.08

  // +x: full-width data bus fanning to the board edge
  {
    const pins = mobile ? 10 : 18
    const edge = BOARD_W / 2 - 0.4
    for (let i = 0; i < pins; i++) {
      const f = i / (pins - 1) - 0.5
      const pinV = f * (CHIP_SIZE - 0.5)
      const laneV = f * BOARD_D * 0.42
      // stagger by position along the diagonal direction: the trace whose
      // lane sits farthest in the fan direction turns first, so diagonals
      // spread apart (separation = (pitch + stagger)/√2) and never cross
      const dir = laneV >= pinV ? 1 : -1
      const u1 = u0 + 0.55 + (0.5 - f * dir) * 1.7
      const u2 = u1 + Math.abs(laneV - pinV)
      const local: [number, number][] = [[u0, pinV], [u1, pinV], [u2, laneV], [Math.max(u2 + 0.5, edge), laneV]]
      traces.push(makeTrace(local.map(([u, v]) => toWorld(true, 1, u, v)), 2, 'pad'))
    }
  }

  // -x: three bank bundles — tight ribbons with clear gaps between groups
  {
    const groups = 3
    const perGroup = mobile ? 4 : 6
    const edge = BOARD_W / 2 - 0.4
    for (let g = 0; g < groups; g++) {
      const center = (g - 1) * 2.9
      for (let j = 0; j < perGroup; j++) {
        const fg = j / (perGroup - 1) - 0.5
        const i = g * perGroup + j
        const fAll = i / (groups * perGroup - 1) - 0.5
        const pinV = fAll * (CHIP_SIZE - 0.5)
        // middle bank runs straight (lanes = pins) so its corridor never
        // threads through the outer banks' escapes; outer banks fan away
        const laneV = g === 1 ? pinV : center + fg * (perGroup - 1) * 0.3
        const dir = laneV >= pinV ? 1 : -1
        const u1 = u0 + 0.6 + (0.5 - fg * dir) * 0.55
        const u2 = u1 + Math.abs(laneV - pinV)
        // the straight middle bank terminates staggered in two pad columns —
        // its lanes sit at pin pitch, too tight for a single row of pads
        const u3 = Math.max(u2 + 0.5, edge) - (g === 1 ? (j % 2) * 0.6 : 0)
        const local: [number, number][] = [[u0, pinV], [u1, pinV], [u2, laneV], [u3, laneV]]
        traces.push(makeTrace(local.map(([u, v]) => toWorld(true, -1, u, v)), 2, 'pad'))
      }
    }
  }

  // +z: short bus into the companion chip, a few lines continue to the edge
  {
    const pins = mobile ? 8 : 14
    const uEnd = DEVICE_U - DEVICE_HALF - 0.06
    for (let i = 0; i < pins; i++) {
      const f = i / (pins - 1) - 0.5
      const pinV = f * (CHIP_SIZE - 0.5)
      const laneV = f * DEVICE_PIN_SPREAD
      const dir = laneV >= pinV ? 1 : -1
      const u1 = u0 + 0.45 + (0.5 - f * dir) * 0.9
      const u2 = u1 + Math.abs(laneV - pinV)
      const local: [number, number][] = [[u0, pinV], [u1, pinV], [u2, laneV], [Math.max(u2 + 0.2, uEnd), laneV]]
      traces.push(makeTrace(local.map(([u, v]) => toWorld(false, 1, u, v)), 2, 'none'))
    }
    const cont = mobile ? 4 : 6
    for (let i = 0; i < cont; i++) {
      const v = (i / (cont - 1) - 0.5) * 2.2
      const raw = [
        toWorld(false, 1, DEVICE_U + DEVICE_HALF + 0.06, v),
        toWorld(false, 1, BOARD_D / 2 - 0.4, v),
      ]
      traces.push(makeTrace(raw, null, 'pad'))
    }
  }

  // -z: connector run ending in gold fingers at the board edge — copper
  // must run INTO each finger pad, not stop short of it
  {
    const pins = mobile ? 8 : 16
    const uEnd = FINGER_U - 0.1
    for (let i = 0; i < pins; i++) {
      const f = i / (pins - 1) - 0.5
      const pinV = f * (CHIP_SIZE - 0.5)
      const laneV = f * 7.0
      const dir = laneV >= pinV ? 1 : -1
      const u1 = u0 + 0.55 + (0.5 - f * dir) * 1.5
      const u2 = u1 + Math.abs(laneV - pinV)
      const local: [number, number][] = [[u0, pinV], [u1, pinV], [u2, laneV], [Math.max(u2 + 0.3, uEnd), laneV]]
      traces.push(makeTrace(local.map(([u, v]) => toWorld(false, -1, u, v)), 2, 'none'))
    }
  }

  return traces
}

// ---------------------------------------------------------------------------
// Copper ribbons with a GPU pulse: each trace is a flat strip whose shader
// runs a bright head + exponential comet tail along its arc length.
// ---------------------------------------------------------------------------
const FOG_DENSITY = 0.026

const traceUniforms = {
  uTime: { value: 0 },
  uSpeed: { value: PULSE_SPEED },
  uBase: { value: new Color('#8a5a30') },
  uPulse: { value: new Color('#FAB95B') },
  uFogColor: { value: new Color('#0a0f1a') },
  uFogDensity: { value: DEBUG_2D ? 0 : FOG_DENSITY },
}

const traceMaterial = new ShaderMaterial({
  uniforms: traceUniforms,
  transparent: true,
  depthWrite: false,
  vertexShader: `
    attribute float aU;
    attribute float aV;
    attribute float aPhase;
    attribute float aLen;
    attribute float aActive;
    varying float vU;
    varying float vV;
    varying float vPhase;
    varying float vLen;
    varying float vActive;
    varying float vDist;
    void main() {
      vU = aU;
      vV = aV;
      vPhase = aPhase;
      vLen = aLen;
      vActive = aActive;
      vec4 mv = modelViewMatrix * vec4(position, 1.0);
      vDist = -mv.z;
      gl_Position = projectionMatrix * mv;
    }
  `,
  fragmentShader: `
    uniform float uTime;
    uniform float uSpeed;
    uniform vec3 uBase;
    uniform vec3 uPulse;
    uniform vec3 uFogColor;
    uniform float uFogDensity;
    varying float vU;
    varying float vV;
    varying float vPhase;
    varying float vLen;
    varying float vActive;
    varying float vDist;
    void main() {
      float head = fract(uTime * uSpeed / vLen + vPhase);
      float behind = fract(head - vU);
      // comet living inside the copper: long soft tail, bright core,
      // white-hot tip right at the front of the pulse
      float tail = exp(-behind * vLen * 1.1);
      float core = exp(-behind * vLen * 9.0) * 2.2;
      float pulse = (tail + core) * vActive;
      vec3 hot = vec3(1.0, 0.96, 0.88) * exp(-behind * vLen * 26.0) * vActive * 1.2;
      // crisp body with a thin anti-aliasing fringe at the edges only —
      // wide falloff reads as blur, copper should read as a solid etch
      float edge = 1.0 - abs(vV * 2.0 - 1.0);
      float alpha = smoothstep(0.0, 0.22, edge);
      vec3 col = uBase * (0.92 + 0.16 * edge) + uPulse * pulse + hot;
      // match the scene's FogExp2 so copper sinks into the same haze
      float fogF = 1.0 - exp(-uFogDensity * uFogDensity * vDist * vDist);
      col = mix(col, uFogColor, clamp(fogF, 0.0, 1.0));
      gl_FragColor = vec4(col, alpha);
    }
  `,
})

function buildRibbons(traces: Trace[]): BufferGeometry {
  const positions: number[] = []
  const aU: number[] = []
  const aV: number[] = []
  const aPhase: number[] = []
  const aLen: number[] = []
  const aActive: number[] = []
  const indices: number[] = []
  const dir = new Vector3()

  for (const trace of traces) {
    const n = trace.pts.length
    const vBase = positions.length / 3
    for (let s = 0; s < n; s++) {
      const p = trace.pts[s]!
      const prev = trace.pts[Math.max(s - 1, 0)]!
      const next = trace.pts[Math.min(s + 1, n - 1)]!
      dir.subVectors(next, prev).normalize()
      // perpendicular in the board plane
      const nx = -dir.z * TRACE_WIDTH / 2
      const nz = dir.x * TRACE_WIDTH / 2
      positions.push(p.x - nx, TRACE_Y, p.z - nz, p.x + nx, TRACE_Y, p.z + nz)
      const u = trace.arc[s]! / trace.length
      aU.push(u, u)
      aV.push(0, 1)
      aPhase.push(trace.phase, trace.phase)
      aLen.push(trace.length, trace.length)
      const act = trace.active ? 1 : 0
      aActive.push(act, act)
      if (s < n - 1) {
        const a = vBase + s * 2
        indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
      }
    }
  }

  const geo = new BufferGeometry()
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3))
  geo.setAttribute('aU', new Float32BufferAttribute(aU, 1))
  geo.setAttribute('aV', new Float32BufferAttribute(aV, 1))
  geo.setAttribute('aPhase', new Float32BufferAttribute(aPhase, 1))
  geo.setAttribute('aLen', new Float32BufferAttribute(aLen, 1))
  geo.setAttribute('aActive', new Float32BufferAttribute(aActive, 1))
  geo.setIndex(indices)
  return geo
}

// ---------------------------------------------------------------------------
// Procedural textures
// ---------------------------------------------------------------------------
function makeBoardTexture() {
  const s = 512
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = s
  const c = canvas.getContext('2d')!
  c.fillStyle = '#ffffff'
  c.fillRect(0, 0, s, s)
  // faint inner copper layer — manhattan runs under the solder mask
  c.strokeStyle = 'rgba(0,0,0,0.10)'
  c.lineWidth = 3
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * s
    const y = Math.random() * s
    const l1 = 30 + Math.random() * 120
    const l2 = 30 + Math.random() * 120
    c.beginPath()
    c.moveTo(x, y)
    c.lineTo(x + l1, y)
    c.lineTo(x + l1, y + l2)
    c.stroke()
  }
  c.strokeStyle = 'rgba(0,0,0,0.14)'
  c.lineWidth = 1
  for (let i = 0; i <= 16; i++) {
    const p = (i / 16) * s
    c.beginPath()
    c.moveTo(p, 0)
    c.lineTo(p, s)
    c.moveTo(0, p)
    c.lineTo(s, p)
    c.stroke()
  }
  c.fillStyle = 'rgba(0,0,0,0.20)'
  for (let i = 0; i < 90; i++) {
    c.beginPath()
    c.arc(Math.random() * s, Math.random() * s, 1.6 + Math.random() * 2.2, 0, Math.PI * 2)
    c.fill()
  }
  const tex = new CanvasTexture(canvas)
  tex.wrapS = tex.wrapT = RepeatWrapping
  tex.repeat.set(5, 3.5)
  return tex
}

function makeDieTexture() {
  const w = 1024
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = w
  const c = canvas.getContext('2d')!

  // brushed-metal lid
  c.fillStyle = '#0b101c'
  c.fillRect(0, 0, w, w)
  for (let i = 0; i < 420; i++) {
    const x = Math.random() * w
    c.strokeStyle = `rgba(${Math.random() < 0.5 ? '255,255,255' : '0,0,0'}, ${0.015 + Math.random() * 0.03})`
    c.lineWidth = 1
    c.beginPath()
    c.moveTo(x, 0)
    c.lineTo(x, w)
    c.stroke()
  }

  // structured die blocks: two cores flanking a striped cache array
  c.strokeStyle = 'rgba(84, 119, 146, 0.28)'
  c.lineWidth = 3
  c.strokeRect(96, 210, 250, 290) // core 0
  c.strokeRect(678, 210, 250, 290) // core 1
  c.strokeStyle = 'rgba(84, 119, 146, 0.18)'
  c.lineWidth = 2
  c.strokeRect(116, 230, 210, 110)
  c.strokeRect(116, 360, 210, 120)
  c.strokeRect(698, 230, 210, 110)
  c.strokeRect(698, 360, 210, 120)
  for (let i = 0; i < 14; i++) { // cache stripes
    c.strokeRect(386 + i * 18, 222, 10, 266)
  }
  c.font = '22px monospace'
  c.fillStyle = 'rgba(84, 119, 146, 0.5)'
  c.textAlign = 'center'
  c.fillText('CORE 0', 221, 530)
  c.fillText('CORE 1', 803, 530)
  c.fillText('L2 CACHE', 512, 530)

  // chip logo + brand, top-left
  c.strokeStyle = 'rgba(205, 214, 228, 0.7)'
  c.lineWidth = 5
  c.strokeRect(84, 84, 72, 72)
  c.strokeRect(104, 104, 32, 32)
  c.lineWidth = 4
  for (let i = 0; i < 3; i++) {
    const o = 98 + i * 22
    for (const [x1, y1, x2, y2] of [[o, 66, o, 80], [o, 160, o, 174], [66, o, 80, o], [160, o, 174, o]] as const) {
      c.beginPath()
      c.moveTo(x1, y1)
      c.lineTo(x2, y2)
      c.stroke()
    }
  }
  c.font = 'bold 40px monospace'
  c.fillStyle = 'rgba(205, 214, 228, 0.85)'
  c.textAlign = 'left'
  c.fillText('shakir.sd', 196, 134)
  c.font = '26px monospace'
  c.fillStyle = 'rgba(205, 214, 228, 0.4)'
  c.fillText('SEMICONDUCTOR DIV.', 196, 168)

  // part markings
  c.textAlign = 'center'
  const grad = c.createLinearGradient(0, 580, 0, 700)
  grad.addColorStop(0, '#ffd9a0')
  grad.addColorStop(1, '#FAB95B')
  c.fillStyle = grad
  c.font = 'bold 150px monospace'
  c.fillText('SHAKIR', w / 2, 700)
  c.fillStyle = 'rgba(232, 226, 219, 0.88)'
  c.font = '46px monospace'
  c.fillText('SA-1 · FULL-STACK SoC', w / 2, 772)
  c.font = '33px monospace'
  c.fillStyle = 'rgba(143, 176, 196, 0.95)'
  c.fillText('WEB · HARDWARE · AI', w / 2, 828)
  c.font = '24px monospace'
  c.fillStyle = 'rgba(205, 214, 228, 0.42)'
  c.fillText('DIFFUSED IN SUDAN · LOT L2547-SDN · ©2026', w / 2, 936)

  // corner fiducials
  c.strokeStyle = 'rgba(205, 214, 228, 0.5)'
  c.lineWidth = 4
  for (const [cx, cy, dx, dy] of [[40, 40, 1, 1], [w - 40, 40, -1, 1], [40, w - 40, 1, -1], [w - 40, w - 40, -1, -1]] as const) {
    c.beginPath()
    c.moveTo(cx + dx * 26, cy)
    c.lineTo(cx, cy)
    c.lineTo(cx, cy + dy * 26)
    c.stroke()
  }

  // pin-1 triangle
  c.fillStyle = '#FAB95B'
  c.beginPath()
  c.moveTo(76, w - 110)
  c.lineTo(116, w - 70)
  c.lineTo(76, w - 70)
  c.closePath()
  c.fill()

  // diagonal laser sheen
  const sheen = c.createLinearGradient(0, 0, w, w)
  sheen.addColorStop(0.42, 'rgba(255,255,255,0)')
  sheen.addColorStop(0.5, 'rgba(255,255,255,0.045)')
  sheen.addColorStop(0.58, 'rgba(255,255,255,0)')
  c.fillStyle = sheen
  c.fillRect(0, 0, w, w)

  const tex = new CanvasTexture(canvas)
  tex.colorSpace = SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

// ---------------------------------------------------------------------------
// Scene construction
// ---------------------------------------------------------------------------
const boardMaterial = new MeshStandardMaterial({ map: makeBoardTexture(), color: new Color('#0d1526'), roughness: 0.82, metalness: 0.25 })
const chipBodyMaterial = new MeshStandardMaterial({ color: new Color('#10182a'), roughness: 0.32, metalness: 0.55 })
const substrateMaterial = new MeshStandardMaterial({ color: new Color('#16203a'), roughness: 0.6, metalness: 0.3 })
const goldMaterial = new MeshStandardMaterial({ color: new Color('#c9963f'), roughness: 0.28, metalness: 1 })
// drilled via barrel: unlit near-black reads as a hole through the board
const holeMaterial = new MeshBasicMaterial({ color: new Color('#06080d') })

// vias are annular rings with a dark drilled center, not solid rivets
function addVias(positions: { x: number, z: number }[], outerR: number) {
  if (!positions.length) return
  const dummy = new Object3D()
  const innerR = outerR * 0.48
  const rings = new InstancedMesh(new RingGeometry(innerR, outerR, 12), goldMaterial, positions.length)
  const holes = new InstancedMesh(new CircleGeometry(innerR, 10), holeMaterial, positions.length)
  positions.forEach((p, i) => {
    dummy.position.set(p.x, 0.118, p.z)
    dummy.rotation.set(-Math.PI / 2, 0, 0)
    dummy.updateMatrix()
    rings.setMatrixAt(i, dummy.matrix)
    dummy.position.y = 0.117
    dummy.updateMatrix()
    holes.setMatrixAt(i, dummy.matrix)
  })
  rings.instanceMatrix.needsUpdate = holes.instanceMatrix.needsUpdate = true
  sceneGroup.add(rings, holes)
}
const dieMaterial = new MeshStandardMaterial({ map: makeDieTexture(), roughness: 0.4, metalness: 0.3, emissive: new Color('#2a3650'), emissiveIntensity: 0.35 })

// component materials — each part type gets its real-world signature
function makeCapSleeveTexture() {
  const w = 256
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = w
  const c = canvas.getContext('2d')!
  c.fillStyle = '#1d2434'
  c.fillRect(0, 0, w, w)
  // polarity stripe down one side of the can
  c.fillStyle = '#9aa6b8'
  c.fillRect(10, 0, 42, w)
  c.fillStyle = '#1d2434'
  c.font = 'bold 30px monospace'
  c.textAlign = 'center'
  for (let i = 0; i < 4; i++) c.fillText('−', 31, 36 + i * 64)
  // capacity marking printed along the can axis
  c.save()
  c.translate(150, w / 2)
  c.rotate(-Math.PI / 2)
  c.fillStyle = 'rgba(216, 222, 232, 0.85)'
  c.font = 'bold 34px monospace'
  c.fillText('470µF 16V', 0, 0)
  c.restore()
  // crimp groove near the top
  c.fillStyle = 'rgba(0,0,0,0.45)'
  c.fillRect(0, 14, w, 8)
  const tex = new CanvasTexture(canvas)
  tex.colorSpace = SRGBColorSpace
  return tex
}

function makeCapTopTexture() {
  const w = 128
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = w
  const c = canvas.getContext('2d')!
  c.fillStyle = '#c3c9d2'
  c.fillRect(0, 0, w, w)
  // stamped vent cross
  c.strokeStyle = 'rgba(70, 78, 92, 0.8)'
  c.lineWidth = 5
  c.beginPath()
  c.moveTo(w / 2, 14)
  c.lineTo(w / 2, w - 14)
  c.moveTo(14, w / 2)
  c.lineTo(w - 14, w / 2)
  c.stroke()
  const tex = new CanvasTexture(canvas)
  tex.colorSpace = SRGBColorSpace
  return tex
}

const capSleeveMaterial = new MeshStandardMaterial({ map: makeCapSleeveTexture(), roughness: 0.45, metalness: 0.2 })
const capTopMaterial = new MeshStandardMaterial({ map: makeCapTopTexture(), roughness: 0.3, metalness: 0.9 })
const silverMaterial = new MeshStandardMaterial({ color: new Color('#b8bec8'), roughness: 0.3, metalness: 1 })
const mlccMaterial = new MeshStandardMaterial({ color: new Color('#b59a6a'), roughness: 0.6, metalness: 0.1 })
const resBodyMaterial = new MeshStandardMaterial({ color: new Color('#15171d'), roughness: 0.5, metalness: 0.2 })
const ledMaterial = new MeshBasicMaterial({ color: new Color('#ffffff'), toneMapped: false })
const silkMaterial = new MeshBasicMaterial({ transparent: true, opacity: 0.85, depthWrite: false })

const sceneGroup = new Group()
const sceneFog = new FogExp2('#0a0f1a', FOG_DENSITY)
let fogAttached = false
let traces: Trace[] = []
let dustPoints: Points | null = null
let dustBase: Float32Array
let dustPhase: Float32Array

// neutral radial falloff (no hot core) for atmosphere sprites
function makeSoftGlow() {
  const s = 128
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = s
  const c = canvas.getContext('2d')!
  const g = c.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2)
  g.addColorStop(0, 'rgba(255,255,255,0.55)')
  g.addColorStop(0.45, 'rgba(255,255,255,0.18)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  c.fillStyle = g
  c.fillRect(0, 0, s, s)
  return new CanvasTexture(canvas)
}

function buildAtmosphere(mobile: boolean) {
  // floating dust motes — the "air" between camera and board
  const dustCount = mobile ? 50 : 130
  dustBase = new Float32Array(dustCount * 3)
  dustPhase = new Float32Array(dustCount)
  for (let i = 0; i < dustCount; i++) {
    dustBase[i * 3] = (Math.random() - 0.5) * 26
    dustBase[i * 3 + 1] = 0.3 + Math.random() * 4.5
    dustBase[i * 3 + 2] = (Math.random() - 0.5) * 18
    dustPhase[i] = Math.random() * Math.PI * 2
  }
  const dustGeo = new BufferGeometry()
  dustGeo.setAttribute('position', new Float32BufferAttribute(dustBase.slice(), 3))
  dustPoints = new Points(dustGeo, new PointsMaterial({
    map: makeSoftGlow(),
    color: new Color('#9bb0c9'),
    size: 0.06,
    transparent: true,
    opacity: 0.5,
    blending: AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  }))
  sceneGroup.add(dustPoints)

  // a few large out-of-focus bokeh discs
  const bokehCount = mobile ? 6 : 12
  const bokehGeo = new BufferGeometry()
  const bokehPos = new Float32Array(bokehCount * 3)
  for (let i = 0; i < bokehCount; i++) {
    bokehPos[i * 3] = (Math.random() - 0.5) * 24
    bokehPos[i * 3 + 1] = 1 + Math.random() * 4
    bokehPos[i * 3 + 2] = (Math.random() - 0.5) * 16
  }
  bokehGeo.setAttribute('position', new Float32BufferAttribute(bokehPos, 3))
  const bokeh = new Points(bokehGeo, new PointsMaterial({
    map: makeSoftGlow(),
    color: new Color('#FAB95B'),
    size: 0.9,
    transparent: true,
    opacity: 0.07,
    blending: AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
    fog: false,
  }))
  sceneGroup.add(bokeh)

  // horizon glows — warm city-glow over the far edge, cool answer opposite
  const glowTex = makeSoftGlow()
  const addGlow = (color: string, opacity: number, pos: [number, number, number], scale: [number, number]) => {
    const sprite = new Sprite(new SpriteMaterial({
      map: glowTex,
      color: new Color(color),
      transparent: true,
      opacity,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
    }))
    sprite.position.set(...pos)
    sprite.scale.set(scale[0], scale[1], 1)
    sceneGroup.add(sprite)
  }
  addGlow('#FAB95B', 0.14, [9, 1.4, -9.5], [20, 7])
  addGlow('#547792', 0.12, [-11, 1.6, -7], [16, 6])
}

function roundedRect(size: number, r: number): Shape {
  const h = size / 2
  const s = new Shape()
  s.moveTo(-h + r, -h)
  s.lineTo(h - r, -h)
  s.quadraticCurveTo(h, -h, h, -h + r)
  s.lineTo(h, h - r)
  s.quadraticCurveTo(h, h, h - r, h)
  s.lineTo(-h + r, h)
  s.quadraticCurveTo(-h, h, -h, h - r)
  s.lineTo(-h, -h + r)
  s.quadraticCurveTo(-h, -h, -h + r, -h)
  return s
}

// blinking SMD LEDs (state shared with the render loop)
interface LedState { base: Color, freq: number, phase: number, blinks: boolean }
let ledMesh: InstancedMesh | null = null
const ledStates: LedState[] = []
const ledTint = new Color()

interface PartSpec { x: number, z: number, rotY: number, scale?: number, label?: string }

function buildComponents(mobile: boolean) {
  const dummy = new Object3D()
  const occupied: { x: number, z: number }[] = []
  const silkLabels: { x: number, z: number, text: string }[] = []
  let cN = 0
  let rN = 0
  let dN = 0
  let xN = 0

  // --- net-aware placement helpers ----------------------------------------

  // closest point on any trace SEGMENT (straight lanes are only two
  // vertices long — vertex sampling misses the whole middle of a run)
  function nearestTrace(x: number, z: number): PartSpec & { dist: number } {
    let bestD = Infinity
    let bx = 0
    let bz = 0
    let brot = 0
    for (const t of traces) {
      for (let i = 0; i < t.pts.length - 1; i++) {
        const a = t.pts[i]!
        const b = t.pts[i + 1]!
        const abx = b.x - a.x
        const abz = b.z - a.z
        const len2 = abx * abx + abz * abz
        if (len2 < 1e-6) continue
        const s = Math.max(0, Math.min(1, ((x - a.x) * abx + (z - a.z) * abz) / len2))
        const px = a.x + abx * s
        const pz = a.z + abz * s
        const d = (px - x) ** 2 + (pz - z) ** 2
        if (d < bestD) {
          bestD = d
          bx = px
          bz = pz
          brot = Math.atan2(-abz, abx)
        }
      }
    }
    return { x: bx, z: bz, rotY: brot, dist: Math.sqrt(bestD) }
  }

  // route a stub from a part to its nearest net the way real copper is
  // routed: a run along the dominant axis finished with an exact 45° jog
  const stubs: Trace[] = []
  const junctions: { x: number, z: number }[] = []
  function pushStubPath(raw: Vector3[], junction?: { x: number, z: number }) {
    const pts = raw.length >= 3 ? filletPath(raw, 0.15) : raw
    const { arc, length } = measure(pts)
    stubs.push({ pts, arc, length, bend: null, endCap: 'none', phase: 0, active: false })
    if (junction) junctions.push(junction)
  }

  function addStub(x: number, z: number) {
    const n = nearestTrace(x, z)
    if (n.dist < 0.45 || n.dist > 3.2) return
    const dx = n.x - x
    const dz = n.z - z
    const raw: Vector3[] = [new Vector3(x, TRACE_Y, z)]
    const run = Math.abs(Math.abs(dx) - Math.abs(dz))
    if (run > 0.08) {
      raw.push(Math.abs(dx) > Math.abs(dz)
        ? new Vector3(x + Math.sign(dx) * run, TRACE_Y, z)
        : new Vector3(x, TRACE_Y, z + Math.sign(dz) * run))
    }
    raw.push(new Vector3(n.x, TRACE_Y, n.z))
    pushStubPath(raw, { x: n.x, z: n.z })
  }

  const elcaps: PartSpec[] = []
  const mlccs: PartSpec[] = []
  const ress: PartSpec[] = []
  const leds: PartSpec[] = []
  const xtals: PartSpec[] = []
  const claim = (s: PartSpec) => {
    occupied.push({ x: s.x, z: s.z })
    return s
  }

  // 1. series parts INLINE on the nets — only on the straight lane run
  // after the bend, so the part never sits rotated across its neighbours
  const inlineCandidates = traces.filter(t => t.bend && t.length > 7)
  const inlineCount = Math.min(mobile ? 6 : 11, inlineCandidates.length)
  const stride = Math.max(1, Math.floor(inlineCandidates.length / inlineCount))
  for (let k = 0; k < inlineCount; k++) {
    const t = inlineCandidates[(k * stride + 2) % inlineCandidates.length]!
    const end = t.pts[t.pts.length - 1]!
    const dx = end.x - t.bend!.x
    const dz = end.z - t.bend!.z
    if (Math.hypot(dx, dz) < 2.4) continue
    const f = 0.35 + (k % 4) * 0.13
    const spot = claim({
      x: t.bend!.x + dx * f,
      z: t.bend!.z + dz * f,
      rotY: Math.atan2(-dz, dx),
    })
    if (k % 2 === 0) ress.push({ ...spot, label: `R${++rN}` })
    else mlccs.push({ ...spot, label: `C${++cN}` })
  }

  // 2. decoupling caps at the CPU's trace-free corners + flanking the SDRAM.
  // Bypass caps don't route to signal nets — they drop straight into the
  // power/ground planes, so each pad gets its own via.
  const addViaPair = (s: PartSpec) => {
    const dx = Math.cos(s.rotY) * 0.32
    const dz = -Math.sin(s.rotY) * 0.32
    junctions.push({ x: s.x + dx, z: s.z + dz }, { x: s.x - dx, z: s.z - dz })
  }
  const addDecap = (s: PartSpec) => {
    mlccs.push(claim(s))
    addViaPair(s)
  }
  for (const [sx, sz] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
    addDecap({ x: sx * 2.05, z: sz * 2.05, rotY: (Math.PI / 4) * sx * sz, label: `C${++cN}` })
    if (!mobile) addDecap({ x: sx * 2.55, z: sz * 2.55, rotY: (Math.PI / 4) * sx * sz, label: `C${++cN}` })
  }
  addDecap({ x: 2.2, z: DEVICE_U, rotY: Math.PI / 2, label: `C${++cN}` })
  addDecap({ x: -2.2, z: DEVICE_U, rotY: Math.PI / 2, label: `C${++cN}` })

  // 3. status LED row near the connector, each with its series resistor.
  // One continuous feed per column: LED → resistor → outer bank rail,
  // wired deterministically (nearest-net search would pull D6 sideways
  // into the connector lanes)
  const ledColors = ['#39d98a', '#FAB95B', '#ff6b5e']
  const ledCount = mobile ? 4 : 6
  const LED_RAIL_Z = -3.65 // outermost west-bank lane
  for (let i = 0; i < ledCount; i++) {
    const x = -7.7 + i * 0.72
    leds.push(claim({ x, z: -6.55, rotY: Math.PI / 2, label: `D${++dN}` }))
    ress.push(claim({ x, z: -5.85, rotY: Math.PI / 2 }))
    pushStubPath(
      [new Vector3(x, TRACE_Y, -6.55), new Vector3(x, TRACE_Y, LED_RAIL_Z)],
      { x, z: LED_RAIL_Z },
    )
  }

  // 4. bulk electrolytics: a spaced 2×2 bank in the power corner, with
  // per-cap size variance so it reads as a family, not a clump
  const elcapCount = mobile ? 2 : 4
  for (let i = 0; i < elcapCount; i++) {
    const s = claim({
      x: 7.0 + (i % 2) * 1.35,
      z: -4.8 - Math.floor(i / 2) * 1.35,
      rotY: i * 1.9, // each can faces a different way: stripes + print vary
      scale: 0.82 + ((i * 7) % 4) * 0.11,
      label: `C${++cN}`,
    })
    elcaps.push(s)
    // lead exits from under the can, not from a point floating beside it
    addStub(s.x, s.z)
  }

  // 5. crystals live next to their ICs — leads exit from under the body
  const x1 = claim({ x: 2.8, z: -2.8, rotY: Math.PI / 4, label: `X${++xN}` })
  xtals.push(x1)
  addStub(x1.x, x1.z)
  if (!mobile) {
    const x2 = claim({ x: -2.6, z: 4.4, rotY: 0, label: `X${++xN}` })
    xtals.push(x2)
    addStub(x2.x, x2.z)
  }

  // 6. a few free parts — aligned to and stubbed into the nearest net.
  // If sampling can't find a clear spot, the part is dropped: a missing
  // part is invisible, an overlapping one is not.
  const freeCount = mobile ? 3 : 6
  for (let i = 0; i < freeCount; i++) {
    let x = 0
    let z = 0
    let found = false
    for (let tries = 0; tries < 80; tries++) {
      x = (Math.random() - 0.5) * (BOARD_W - 3)
      z = (Math.random() - 0.5) * (BOARD_D - 3)
      if (Math.abs(x) < 3.6 && Math.abs(z) < 3.6) continue // CPU keep-out
      if (Math.abs(x) < 2.8 && z > 4.0 && z < 7.4) continue // companion chip
      if (z < -(FINGER_U - 1.4)) continue // edge-connector strip
      if (occupied.some(p => (p.x - x) ** 2 + (p.z - z) ** 2 < 1.2)) continue
      if (nearestTrace(x, z).dist < 0.4) continue // never sit ON a net
      found = true
      break
    }
    if (!found) continue
    const near = nearestTrace(x, z)
    const s = claim({ x, z, rotY: near.rotY, label: i % 2 === 0 ? `R${++rN}` : `C${++cN}` })
    if (i % 2 === 0) ress.push(s)
    else mlccs.push(s)
    addStub(x, z)
  }

  // --- instancing -----------------------------------------------------------
  const fill = (mesh: InstancedMesh, specs: PartSpec[], y: number) => {
    specs.forEach((s, i) => {
      dummy.position.set(s.x, y * (s.scale ?? 1), s.z)
      dummy.rotation.set(0, s.rotY, 0)
      dummy.scale.setScalar(s.scale ?? 1)
      dummy.updateMatrix()
      mesh.setMatrixAt(i, dummy.matrix)
      if (s.label) silkLabels.push({ x: s.x, z: s.z, text: s.label })
    })
    dummy.scale.setScalar(1)
    mesh.instanceMatrix.needsUpdate = true
    return mesh
  }
  const endCaps = (mesh: InstancedMesh, body: InstancedMesh, specs: PartSpec[], offset: number) => {
    const m = new Matrix4()
    specs.forEach((_, i) => {
      body.getMatrixAt(i, m)
      mesh.setMatrixAt(i * 2, m.clone().multiply(new Matrix4().makeTranslation(-offset, 0, 0)))
      mesh.setMatrixAt(i * 2 + 1, m.clone().multiply(new Matrix4().makeTranslation(offset, 0, 0)))
    })
    mesh.instanceMatrix.needsUpdate = true
    return mesh
  }

  const sleeves = fill(new InstancedMesh(new CylinderGeometry(0.22, 0.22, 0.55, 14), capSleeveMaterial, elcaps.length), elcaps, 0.375)
  const canTops = new InstancedMesh(new CylinderGeometry(0.225, 0.225, 0.06, 14), capTopMaterial, elcaps.length)
  {
    const m = new Matrix4()
    elcaps.forEach((_, i) => {
      sleeves.getMatrixAt(i, m)
      canTops.setMatrixAt(i, m.clone().multiply(new Matrix4().makeTranslation(0, 0.3, 0)))
    })
    canTops.instanceMatrix.needsUpdate = true
  }
  sceneGroup.add(sleeves, canTops)

  const mlcc = fill(new InstancedMesh(new BoxGeometry(0.3, 0.12, 0.18), mlccMaterial, mlccs.length), mlccs, 0.16)
  sceneGroup.add(mlcc, endCaps(new InstancedMesh(new BoxGeometry(0.07, 0.13, 0.19), silverMaterial, mlccs.length * 2), mlcc, mlccs, 0.13))

  const resBody = fill(new InstancedMesh(new BoxGeometry(0.42, 0.12, 0.2), resBodyMaterial, ress.length), ress, 0.16)
  sceneGroup.add(resBody, endCaps(new InstancedMesh(new BoxGeometry(0.09, 0.13, 0.21), silverMaterial, ress.length * 2), resBody, ress, 0.18))

  ledMesh = fill(new InstancedMesh(new BoxGeometry(0.14, 0.05, 0.1), ledMaterial, leds.length), leds, 0.125)
  ledStates.length = 0
  leds.forEach((_, i) => {
    const base = new Color(ledColors[i % ledColors.length])
    ledStates.push({ base, freq: 1.5 + Math.random() * 3, phase: Math.random() * Math.PI * 2, blinks: i % 2 === 0 })
    ledMesh!.setColorAt(i, base)
  })
  sceneGroup.add(ledMesh, endCaps(new InstancedMesh(new BoxGeometry(0.06, 0.06, 0.11), silverMaterial, leds.length * 2), ledMesh, leds, 0.09))

  sceneGroup.add(fill(new InstancedMesh(new BoxGeometry(0.5, 0.18, 0.32), silverMaterial, xtals.length), xtals, 0.19))

  // stub nets tie free parts into the circuit; ringed vias mark every
  // junction (decoupling pads carry via pairs without stubs)
  if (stubs.length) sceneGroup.add(new Mesh(buildRibbons(stubs), traceMaterial))
  addVias(junctions, 0.07)

  // silkscreen: designators, IC labels, board outline + title block
  silkLabels.push(
    { x: 0, z: -(CHIP_SIZE / 2 + 0.55), text: 'U1' },
    { x: -3.9, z: DEVICE_U, text: 'U2 SDRAM' },
    { x: 4.6, z: -(FINGER_U - 0.15), text: 'J1' },
  )
  const SILK_W = 2048
  const SILK_H = Math.round(SILK_W * BOARD_D / BOARD_W)
  const canvas = document.createElement('canvas')
  canvas.width = SILK_W
  canvas.height = SILK_H
  const c = canvas.getContext('2d')!
  c.strokeStyle = 'rgba(255,255,255,0.5)'
  c.lineWidth = 5
  c.strokeRect(22, 22, SILK_W - 44, SILK_H - 44)
  c.fillStyle = 'rgba(255,255,255,0.78)'
  c.font = '34px monospace'
  c.textAlign = 'left'
  c.fillText('shakir.sd · SA-1 DEVBOARD · REV 2.6', 60, SILK_H - 60)
  c.textAlign = 'center'
  c.font = '27px monospace'
  for (const l of silkLabels) {
    const px = (l.x / BOARD_W + 0.5) * SILK_W
    const py = (l.z / BOARD_D + 0.5) * SILK_H - 28
    c.fillText(l.text, px, py)
  }
  const silkTex = new CanvasTexture(canvas)
  silkTex.anisotropy = 8
  silkMaterial.map = silkTex
  const silk = new Mesh(new PlaneGeometry(BOARD_W, BOARD_D), silkMaterial)
  silk.rotation.x = -Math.PI / 2
  silk.position.y = 0.106
  sceneGroup.add(silk)
}

function buildScene() {
  const mobile = isMobile.value
  traces = buildTraces(mobile)

  // board
  const board = new Mesh(new BoxGeometry(BOARD_W, 0.2, BOARD_D), boardMaterial)
  sceneGroup.add(board)

  // copper ribbons with GPU pulses
  sceneGroup.add(new Mesh(buildRibbons(traces), traceMaterial))

  // --- CPU package ---
  const substrateTop = 0.17
  const substrate = new Mesh(new BoxGeometry(CHIP_SIZE + 0.5, 0.07, CHIP_SIZE + 0.5), substrateMaterial)
  substrate.position.y = substrateTop - 0.035
  sceneGroup.add(substrate)

  const PKG_DEPTH = 0.26
  const PKG_BEVEL = 0.05
  const pkg = new Mesh(
    new ExtrudeGeometry(roundedRect(CHIP_SIZE - PKG_BEVEL * 2, 0.16), {
      depth: PKG_DEPTH,
      bevelEnabled: true,
      bevelThickness: PKG_BEVEL,
      bevelSize: PKG_BEVEL,
      bevelSegments: 3,
      curveSegments: 6,
    }),
    chipBodyMaterial,
  )
  pkg.rotation.x = -Math.PI / 2
  pkg.position.y = substrateTop + PKG_BEVEL
  sceneGroup.add(pkg)

  const dieTop = substrateTop + PKG_BEVEL + PKG_DEPTH + PKG_BEVEL
  const die = new Mesh(new PlaneGeometry(CHIP_SIZE * 0.8, CHIP_SIZE * 0.8), dieMaterial)
  die.rotation.x = -Math.PI / 2
  die.position.y = dieTop + 0.003
  sceneGroup.add(die)

  // gold gull-wing leads matching each side's pin count
  const dummy = new Object3D()
  const sidePins: [boolean, number, number][] = [
    [true, 1, mobile ? 10 : 18],
    [true, -1, mobile ? 12 : 18],
    [false, 1, mobile ? 8 : 14],
    [false, -1, mobile ? 8 : 16],
  ]
  const leadCount = sidePins.reduce((sum, [, , n]) => sum + n, 0)
  const leads = new InstancedMesh(new BoxGeometry(0.3, 0.06, 0.1), goldMaterial, leadCount)
  let li = 0
  for (const [horizontal, sign, pins] of sidePins) {
    for (let i = 0; i < pins; i++) {
      const f = i / (pins - 1) - 0.5
      const pinV = f * (CHIP_SIZE - 0.5)
      const u = CHIP_SIZE / 2 + 0.02
      dummy.position.set(
        horizontal ? sign * u : pinV,
        0.135,
        horizontal ? pinV : sign * u,
      )
      dummy.rotation.set(0, horizontal ? 0 : Math.PI / 2, 0)
      dummy.updateMatrix()
      leads.setMatrixAt(li++, dummy.matrix)
    }
  }
  leads.instanceMatrix.needsUpdate = true
  sceneGroup.add(leads)

  // companion memory chip on the +z bus
  const device = new Mesh(new BoxGeometry(3.4, 0.22, 1.5), chipBodyMaterial)
  device.position.set(0, 0.21, DEVICE_U)
  sceneGroup.add(device)
  const devicePins = mobile ? 8 : 14
  const deviceCont = mobile ? 4 : 6
  const deviceLeads = new InstancedMesh(new BoxGeometry(0.1, 0.06, 0.26), goldMaterial, devicePins + deviceCont)
  let dl = 0
  for (let i = 0; i < devicePins; i++) {
    const f = i / (devicePins - 1) - 0.5
    dummy.position.set(f * DEVICE_PIN_SPREAD, 0.135, DEVICE_U - DEVICE_HALF - 0.05)
    dummy.rotation.set(0, 0, 0)
    dummy.updateMatrix()
    deviceLeads.setMatrixAt(dl++, dummy.matrix)
  }
  for (let i = 0; i < deviceCont; i++) {
    const v = (i / (deviceCont - 1) - 0.5) * 2.2
    dummy.position.set(v, 0.135, DEVICE_U + DEVICE_HALF + 0.05)
    dummy.updateMatrix()
    deviceLeads.setMatrixAt(dl++, dummy.matrix)
  }
  deviceLeads.instanceMatrix.needsUpdate = true
  sceneGroup.add(deviceLeads)

  // gold fingers at the -z edge connector
  const fingerCount = mobile ? 8 : 16
  const fingers = new InstancedMesh(new BoxGeometry(0.16, 0.05, 0.9), goldMaterial, fingerCount)
  for (let i = 0; i < fingerCount; i++) {
    const f = i / (fingerCount - 1) - 0.5
    dummy.position.set(f * 7.0, 0.125, -FINGER_U)
    dummy.rotation.set(0, 0, 0)
    dummy.updateMatrix()
    fingers.setMatrixAt(i, dummy.matrix)
  }
  fingers.instanceMatrix.needsUpdate = true
  sceneGroup.add(fingers)

  // vias where a trace's diagonal meets its lane
  addVias(
    traces.filter((t, i) => t.bend && i % 2 === 0).map(t => ({ x: t.bend!.x, z: t.bend!.z })),
    0.085,
  )

  // solder pads where traces end in the open (not at leads or fingers)
  const padTraces = traces.filter(t => t.endCap === 'pad')
  const pads = new InstancedMesh(new CylinderGeometry(0.1, 0.1, 0.05, 10), goldMaterial, padTraces.length)
  padTraces.forEach((t, i) => {
    const end = t.pts[t.pts.length - 1]!
    dummy.position.set(end.x, 0.12, end.z)
    dummy.rotation.set(0, 0, 0)
    dummy.updateMatrix()
    pads.setMatrixAt(i, dummy.matrix)
  })
  pads.instanceMatrix.needsUpdate = true
  sceneGroup.add(pads)

  buildComponents(mobile)
  if (!DEBUG_2D) buildAtmosphere(mobile)
}

buildScene()

// theme-aware palette
const colorMode = useColorMode()
watchEffect(() => {
  const dark = colorMode.value === 'dark'
  boardMaterial.color.set(dark ? '#0d1526' : '#cfc7b8')
  silkMaterial.color.set(dark ? '#ffffff' : '#2a3242')
  traceUniforms.uBase.value.set(dark ? '#8a5a30' : '#1A3263')
  traceUniforms.uPulse.value.set(dark ? '#FAB95B' : '#FAB95B')
  sceneFog.color.set(dark ? '#0a0f1a' : '#e9e4da')
  traceUniforms.uFogColor.value.copy(sceneFog.color)
})

// ---------------------------------------------------------------------------
// Camera flythrough — Catmull-Rom path with piecewise-lerped look targets:
// corner skim → climb across the board → approach → top-down on the die.
// ---------------------------------------------------------------------------
const camPath = new CatmullRomCurve3([
  new Vector3(-11.5, 1.0, 7.6),
  new Vector3(-5.5, 3.4, 4.2),
  new Vector3(-1.8, 2.8, 1.9),
  new Vector3(0, 4.8, 0.5),
], false, 'centripetal')

const lookTargets = [
  // opening target sits high so the board horizon stays in the lower third
  // of the frame — the hero text gets clean sky in both themes
  new Vector3(-2, 2.1, -2),
  new Vector3(0.5, 0.1, -0.5),
  new Vector3(0, 0.3, 0),
  new Vector3(0, 0.3, 0),
]

const cameraRef = shallowRef<PerspectiveCamera | null>(null)
const lookAt = new Vector3()
const smoothMouse = { x: 0, y: 0 }
const targetMouse = { x: 0, y: 0 }

function onMouseMove(event: MouseEvent) {
  targetMouse.x = (event.clientX / window.innerWidth) * 2 - 1
  targetMouse.y = -(event.clientY / window.innerHeight) * 2 + 1
}

const { onLoop } = useRenderLoop()

onLoop(({ elapsed }) => {
  if (prefersReducedMotion.value) return

  // FogExp2 lives on the scene, which only exists once Tres mounts us
  if (!fogAttached && sceneGroup.parent && !DEBUG_2D) {
    ;(sceneGroup.parent as Scene).fog = sceneFog
    fogAttached = true
  }

  traceUniforms.uTime.value = elapsed

  // dust drifts slowly, each mote on its own phase
  if (dustPoints) {
    const dustAttr = dustPoints.geometry.getAttribute('position') as BufferAttribute
    const dust = dustAttr.array as Float32Array
    for (let i = 0; i < dustPhase.length; i++) {
      const ph = dustPhase[i]!
      dust[i * 3] = dustBase[i * 3]! + Math.sin(elapsed * 0.11 + ph) * 0.9
      dust[i * 3 + 1] = dustBase[i * 3 + 1]! + Math.sin(elapsed * 0.17 + ph * 1.7) * 0.35
      dust[i * 3 + 2] = dustBase[i * 3 + 2]! + Math.cos(elapsed * 0.13 + ph) * 0.7
    }
    dustAttr.needsUpdate = true
  }

  // status LEDs: steady glow or hard on/off blink, each on its own clock
  if (ledMesh) {
    ledStates.forEach((s, i) => {
      const level = s.blinks
        ? (Math.sin(elapsed * s.freq + s.phase) > 0.15 ? 1 : 0.12)
        : 0.85 + Math.sin(elapsed * 2.3 + s.phase) * 0.15
      ledMesh!.setColorAt(i, ledTint.copy(s.base).multiplyScalar(level))
    })
    ledMesh.instanceColor!.needsUpdate = true
  }

  // the die breathes
  dieMaterial.emissiveIntensity = 0.35 + Math.sin(elapsed * 1.2) * 0.12

  // layout-editor view: straight-down, tall + narrow FOV ≈ orthographic,
  // so parts at the board edges don't lean from perspective
  if (DEBUG_2D) {
    const cam = cameraRef.value
    if (cam) {
      cam.position.set(0, 70, 0.001)
      cam.lookAt(0, 0, 0)
      if (cam.fov !== 16) {
        cam.fov = 16
        cam.updateProjectionMatrix()
      }
    }
    return
  }

  // camera along the flight path
  const cam = cameraRef.value
  if (cam) {
    const p = Math.min(Math.max(props.progress, 0), 1)
    smoothMouse.x += (targetMouse.x - smoothMouse.x) * 0.05
    smoothMouse.y += (targetMouse.y - smoothMouse.y) * 0.05

    camPath.getPoint(p, cam.position)
    const idle = (1 - p) * 0.18
    // the shot locks as the camera docks on the die: mouse sway fades out
    // over p 0.7→0.95 so the closing frame holds perfectly still
    const sway = 1 - Math.min(Math.max((p - 0.7) / 0.25, 0), 1)
    cam.position.x += Math.sin(elapsed * 0.4) * idle + smoothMouse.x * 0.35 * sway
    cam.position.y += Math.sin(elapsed * 0.27) * idle * 0.5 + smoothMouse.y * 0.2 * sway

    const seg = Math.min(p * 3, 2.999)
    const i = Math.floor(seg)
    lookAt.lerpVectors(lookTargets[i]!, lookTargets[i + 1]!, seg - i)
    cam.lookAt(lookAt)

    const fov = 50 - p * 12
    if (cam.fov !== fov) {
      cam.fov = fov
      cam.updateProjectionMatrix()
    }
  }
})

onMounted(() => {
  window.addEventListener('mousemove', onMouseMove, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove)
})
</script>

<template>
  <div class="absolute inset-0 overflow-hidden">
    <div v-if="prefersReducedMotion" class="absolute inset-0 bg-gradient-to-br from-[var(--color-dark-base)] via-[var(--color-deep-blue)] to-[var(--color-dark-base)]" />
    <template v-else>
      <!-- night sky behind the board: darker zenith, faint scatter band at the horizon -->
      <div class="absolute inset-0 hidden dark:block" style="background: linear-gradient(180deg, #05070d 0%, #080d18 38%, #13203a 56%, #0a0f1a 72%)" />
      <TresCanvas
        class="!absolute !inset-0"
        :alpha="true"
        :antialias="true"
        window-size
        power-preference="high-performance"
        :clear-color="0x000000"
      >
        <TresPerspectiveCamera ref="cameraRef" :position="[-11.5, 1.0, 7.6]" :fov="50" :near="0.1" :far="80" />

        <TresAmbientLight :intensity="0.5" color="#9bb0c9" />
        <TresDirectionalLight :position="[6, 9, 4]" :intensity="0.9" color="#fff4e0" />
        <TresPointLight :position="[0, 3.5, 0]" :intensity="22" color="#FAB95B" :distance="14" />
        <TresPointLight :position="[-7, 2.5, -4]" :intensity="10" color="#547792" :distance="16" />

        <primitive :object="sceneGroup" />
      </TresCanvas>
    </template>
  </div>
</template>
